import { useEffect, useRef } from "react";

import {
  tinykeys,
  KeyBindingMap,
  KeyBindingOptions,
} from "../helpers/tinykeys";

interface UseBaseOptions extends KeyBindingOptions {
  /**
   * Target element to bind keybindings to (default: window)
   */
  target?: Window | HTMLElement | null;
  /**
   * Whether the keybindings should be active (default: true)
   */
  enabled?: boolean;
}

/**
 * React hook for handling keyboard shortcuts using tinykeys
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   useKeybindings({
 *     'j': () => console.log('Down'),
 *     'k': () => console.log('Up'),
 *     'g g': () => console.log('Go to top'),
 *     'Shift+/': () => console.log('Show help'),
 *     '$mod+k': () => console.log('Command palette'),
 *   });
 *
 *   return <div>Press j/k to navigate</div>;
 * }
 * ```
 */
function useBase(keyBindingMap: KeyBindingMap, options: UseBaseOptions = {}) {
  const {
    target = typeof window !== "undefined" ? window : null,
    enabled = true,
    ...tinykeysOptions
  } = options;

  // Store the cleanup function
  const cleanupRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (!enabled || !target) {
      return;
    }

    cleanupRef.current = tinykeys(target, keyBindingMap, tinykeysOptions);

    return () => {
      if (cleanupRef.current) {
        cleanupRef.current();
        cleanupRef.current = null;
      }
    };
  }, [keyBindingMap, target, enabled, tinykeysOptions]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (cleanupRef.current) {
        cleanupRef.current();
        cleanupRef.current = null;
      }
    };
  }, []);
}

/**
 * Hook for element-scoped keybindings
 * @returns a ref to attach to the target element
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const ref = useElementKeybindings<HTMLDivElement>({
 *     'Enter': () => console.log('Enter pressed'),
 *     'Escape': () => console.log('Escape pressed'),
 *   });
 *
 *   return <div ref={ref} tabIndex={0}>Focusable element</div>;
 * }
 * ```
 */
export function useElementKeybindings<T extends HTMLElement>(
  keyBindingMap: KeyBindingMap,
  options: Omit<UseBaseOptions, "target"> = {},
) {
  const elementRef = useRef<T>(null);

  useBase(keyBindingMap, {
    ...options,
    target: elementRef.current,
  });

  return elementRef;
}

/**
 * Hook for conditional keybindings based on focus state
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   useFocusKeybindings({
 *     'j': () => selectNext(),
 *     'k': () => selectPrevious(),
 *   }, {
 *     // Only active when body or specific elements are focused
 *     allowedElements: ['BODY', 'DIV'],
 *   });
 *
 *   return <div>Navigation with j/k</div>;
 * }
 * ```
 */
export function useKeybindings(
  keyBindingMap: KeyBindingMap,
  options: UseBaseOptions & {
    allowedElements?: string[];
    disallowedElements?: string[];
  } = {},
) {
  const {
    allowedElements = [],
    disallowedElements = ["INPUT", "TEXTAREA", "SELECT"],
    ...keybindingOptions
  } = options;

  // Wrap each handler to check focus state
  const modifiedMap: KeyBindingMap = {};
  Object.keys(keyBindingMap).forEach((key) => {
    modifiedMap[key] = (event: KeyboardEvent) => {
      const activeElement = document.activeElement;
      const tagName = activeElement?.tagName;

      if (activeElement && (activeElement as HTMLElement).isContentEditable) {
        return null;
      }

      if (tagName) {
        if (
          disallowedElements.includes(tagName) ||
          (allowedElements.length > 0 && !allowedElements.includes(tagName))
        ) {
          return null;
        }
      }

      keyBindingMap[key](event);
    };
  });

  useBase(modifiedMap, keybindingOptions);
}
